// src/screens/StudentSessionDetails.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Phone,
  Clock,
  MapPin,
  NotebookPen,
  X,
} from "lucide-react";
import whatsappIcon from "../assets/whatsapp-icon.svg";
import { PrimaryButton, SecondaryButton, TertiaryButton } from "../components/Button";
import TrainerCardHeader from "../components/TrainerCardHeader";
import trainerImg from "../assets/trainer.png";
import { ConfirmCancelModal, CancelledSuccessModal } from "../components/CancelSessionFlow";
import Navbar from "../components/Navbar";
import PageTitle from "../components/PageTitle";
import { cancelSession, getTrainerProfile } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { parseTimeFromSlot, timeToMinutes } from "../utils/timeUtils";
import { openSupportWhatsApp, openTrainerWhatsApp } from "../constants/support";


export default function StudentSessionDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { state } = location;

  // Session data from navigation state
  const sessionData = state?.session;

  // State management
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trainerProfile, setTrainerProfile] = useState<any>(null);
  const [error, setError] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [refundCredits, setRefundCredits] = useState(0);

  const calculateRefundableCredits = () => {
    if (!sessionData || !trainerProfile) return 0;
    if (!sessionData.time) return 0;
    const timeSlot = parseTimeFromSlot(sessionData.time) as { start: string; end: string };
    const startTime = timeSlot?.start || '';
    const endTime = timeSlot?.end || '';
    if (!startTime || !endTime) return 0;
    const durationMinutes = timeToMinutes(endTime) - timeToMinutes(startTime);
    if (durationMinutes <= 0) return 0;
    const creditsPerHour = trainerProfile.creditsPerHour || 1;
    return Math.ceil((durationMinutes / 60) * creditsPerHour);
  };

  // Fetch trainer profile if we have trainer data
  useEffect(() => {
    const fetchTrainerProfile = async () => {
      console.log('[SessionDetails] sessionData:', sessionData);
      console.log('[SessionDetails] trainerId:', sessionData?.trainerId);

      if (!sessionData?.trainerId) {
        console.warn('[SessionDetails] No trainerId found - skipping trainer profile fetch');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const trainerResponse = await getTrainerProfile(sessionData.trainerId);
        console.log('[SessionDetails] trainerResponse:', trainerResponse);
        // API returns trainer data at root level (not nested under a "trainer" key)
        const profile = trainerResponse.trainer || trainerResponse;
        console.log('[SessionDetails] trainerProfile set to:', profile);
        console.log('[SessionDetails] signedProfilePic:', profile?.signedProfilePic);
        console.log('[SessionDetails] profilePic:', profile?.profilePic);
        setTrainerProfile(profile);
      } catch (error) {
        console.error('Error fetching trainer profile:', error);
        setError('Failed to load trainer details');
      } finally {
        setLoading(false);
      }
    };

    fetchTrainerProfile();
  }, [sessionData?.trainerId]);

  // Format session data for display
  const formatSessionData = () => {
    if (!sessionData) {
      return {
        dateLabel: "Unknown Date",
        time: "Unknown Time",
        duration: "Unknown Duration",
        countdown: "",
        trainer: {
          name: "Unknown Trainer",
          rate: 0,
          age: 0,
          sex: "N/A" as const,
          image: trainerImg as unknown as string,
        },
        location: {
          type: "Unknown",
          address: "Unknown Address",
        },
        notes: "No notes available",
      };
    }

    // Use the pre-formatted data from the API
    const dateLabel = sessionData.date ? `${sessionData.date}, ${new Date().toLocaleDateString('en-US', { weekday: 'long' })}` : "Unknown Date";
    const time = sessionData.time || "Unknown Time";

    // Calculate duration from the time string
    let duration = "Unknown Duration";
    if (sessionData.time) {
      const timeSlot = parseTimeFromSlot(sessionData.time) as { start: string; end: string };
      const startTime = timeSlot?.start || '';
      const endTime = timeSlot?.end || '';
      if (startTime && endTime) {
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);
        const durationMinutes = endMinutes - startMinutes;
        duration = `${durationMinutes} mins Session`;
      }
    }

    // Use the countdown from the API if available
    const countdown = sessionData.sessionIn || "";

    return {
      dateLabel,
      time,
      duration,
      countdown,
      trainer: {
        name: trainerProfile?.name || sessionData.trainer || "Unknown Trainer",
        // Prefer the rate we already know from the session; fall back to profile; never hard-default to 0 unless truly missing
        rate:
          sessionData.trainerCreditsPerHour ??
          trainerProfile?.creditsPerHour ??
          undefined,
        age: trainerProfile?.age ?? sessionData.trainerAge ?? undefined,
        sex: (trainerProfile?.sex ?? sessionData.trainerSex ?? "N/A"),
        image:
          trainerProfile?.signedProfilePic ||
          trainerProfile?.profilePic ||
          (trainerImg as unknown as string),
      },
      location: {
        type: sessionData.location || "HOME",
        address: sessionData.address || "Your preferred location",
      },
      notes: sessionData.notes || "No notes available",
    };
  };

  const session = formatSessionData();

  const onSupport = () => {
    openSupportWhatsApp();
  };

  const trainerPhone =
    trainerProfile?.whatsappNumber ||
    trainerProfile?.phoneNumber ||
    sessionData?.trainerPhoneNumber ||
    sessionData?.phoneNumber ||
    sessionData?.whatsappNumber ||
    "";

  const onMessage = () => {
    if (!trainerPhone) {
      alert("Trainer phone number not available.");
      return;
    }
    openTrainerWhatsApp(trainerPhone);
  };

  const onCall = () => {
    if (!trainerPhone) {
      alert("Trainer phone number not available.");
      return;
    }
    alert(`Call trainer at: ${trainerPhone}`);
  };

  const onCancel = () => {
    setCancelError("");
    setShowConfirm(true);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="h-[100dvh] w-full bg-[#F2F2F2] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading session details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="h-[100dvh] w-full bg-[#F2F2F2] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-[#D62422] font-semibold"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-full bg-transparent overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="mx-auto w-full min-h-full flex flex-col">
        {/* Dark header section with Navbar inside */}
        <div className="w-full bg-[linear-gradient(157.07deg,#3a3a3a_0%,#252525_81.65%)] text-white">
          {/* NAVBAR inside the gradient header - no padding */}
          <Navbar
            onBack={() => navigate(-1)}
            background="transparent"
            spacerHeight={40}
            right={
              <button
                onClick={onSupport}
                className="bg-black border border-white/80 px-3 py-2 flex items-center gap-2 text-sm hover:bg-black/80 text-white"
              >
                <img src={whatsappIcon} alt="WhatsApp" className="w-4 h-4" style={{ filter: 'brightness(0) invert(1)' }} />
                <span>Support</span>
              </button>
            }
          />

          {/* Date & time content - centered with padding */}
          <div className="px-5 pb-6 pt-10">
            {/* Date & time */}
            <div className="mb-4">
              <p className="font-manrope font-extrabold text-[16px] leading-[20px] text-[#FA403F] mb-2">
                {session.dateLabel}
              </p>
              <h1 className="font-manrope font-bold text-[24px] leading-[34px] text-left text-white mb-2">
                {session.time}
              </h1>
              <div className="flex items-center gap-2 text-gray-300 pt-3 pb-5">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{session.duration}</span>
              </div>
            </div>

            {/* Countdown bar inside header - only show if countdown exists */}
            {session.countdown && (
              <div className="mt-4 bg-black/60 border border-[#FA403F] px-4 py-3 flex items-center justify-between">
                <span className="text-sm">Starts in</span>
                <span className="text-sm font-semibold">{session.countdown}</span>
              </div>
            )}
          </div>
        </div>

        {/* White content section */}
        <div className="flex-1 bg-white">
          <div className="px-5 py-6 space-y-8">
            {/* SESSION DETAILS */}
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-4">
                // SESSION DETAILS
              </p>

              {/* Trainer banner card */}
              <div className="bg-white border border-gray-200 overflow-visible">
                {/* Reuse the shared banner header used across the app */}
                <TrainerCardHeader
                  name={session.trainer.name}
                  rate={session.trainer.rate}
                  meta={`${session.trainer.age}${session.trainer.sex}`}
                  imageSrc={session.trainer.image}
                  variant="dark"
                  height="auto"
                  imgHeight={140}
                  fillToImageTop
                />

                {/* Actions */}
                <div className="p-4 space-y-3">
                  <button
                    onClick={onMessage}
                    className="w-full bg-[#D62422] text-white py-3 px-4 flex items-center justify-between font-semibold hover:bg-[#B91C1C] transition-colors"
                  >
                    <span>MESSAGE TRAINER</span>
                    <img src={whatsappIcon} alt="WhatsApp" className="w-5 h-5" style={{ filter: 'brightness(0) invert(1)' }} />
                  </button>
                  <button
                    onClick={onCall}
                    className="w-full bg-white border border-[#252525] text-[#252525] py-3 px-4 flex items-center justify-between font-semibold hover:bg-[#252525] hover:text-white transition-colors"
                  >
                    <span>CALL TRAINER</span>
                    <Phone className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* LOCATION */}
            <div className="space-y-2">
              <div className="flex items-center text-gray-500 text-xs uppercase tracking-wide">
                <MapPin className="w-4 h-4 mr-2" />
                <span>Location</span>
              </div>
              <div className="text-black font-semibold">{session.location.type}</div>
              <p className="text-[#5E5E5E] text-sm">{session.location.address}</p>
            </div>

            {/* NOTES */}
            <div>
              <div className="flex items-center text-gray-500 text-xs uppercase tracking-wide mb-2">
                <NotebookPen className="w-4 h-4 mr-2" />
                <span>Notes</span>
              </div>
              <div className="bg-white border border-gray-200 p-4">
                <p className="text-black text-sm leading-relaxed">{session.notes}</p>
              </div>
            </div>

            {/* CANCEL */}
            <div className="pt-2 pb-[max(16px,env(safe-area-inset-bottom))]">
              <SecondaryButton
                label="CANCEL SESSION"
                icon={X}
                onClick={onCancel}
                className="!w-full"
              />
              {cancelError && (
                <p className="mt-3 text-center text-sm text-red-600">{cancelError}</p>
              )}
            </div>
          </div>
        </div>

        {/* ------- modals ------- */}
        <ConfirmCancelModal
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          onKeep={() => setShowConfirm(false)}
          onConfirm={async () => {
            const bookingId = sessionData?.bookingId || sessionData?._id || sessionData?.id;
            if (!bookingId) {
              setCancelError("Missing booking details. Please try again.");
              setShowConfirm(false);
              return;
            }
            try {
              const response = await cancelSession(bookingId);
              const fallbackCredits = calculateRefundableCredits();
              const refunded = Number(response?.refundedCredits || 0);
              setRefundCredits(refunded > 0 ? refunded : fallbackCredits);
              setShowConfirm(false);
              setShowDone(true);
            } catch (cancelErr: any) {
              const message = cancelErr?.response?.data?.message || "Failed to cancel session.";
              setCancelError(message);
              setShowConfirm(false);
            } finally {
            }
          }}
        />

        <CancelledSuccessModal
          open={showDone}
          refundableCredits={refundCredits || calculateRefundableCredits()}
          onClose={() => setShowDone(false)}
          onBookNew={() => {
            setShowDone(false);
            navigate("/explore-trainers"); // or your booking flow route
          }}
        />
      </div>
    </div>
  );
}
